import { useWindowDimensions } from '../utils/helpers';
import { CustomerStoryFilterObject, FilterCategories } from '../utils/types';
import { useState } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { CustomerStoryFilterModalRadioButton } from './customer-story-filter-modal-grouping';

type ComplexModalFilterCategorySectionProps = {
	filterCategory: FilterCategories;
	filterObject: CustomerStoryFilterObject;
	updateFilterObject: (filter: FilterCategories, subFilterName: string) => void;
};

const MAX_SUBFILTERS_BEFORE_EXPAND = 8;
const MAX_COLUMN_ENTRIES_BEFORE_EXPAND = 4;
const MAX_SUBFILTERS_BEFORE_TWO_COLUMNS = 5;
const NUMBER_OF_COLUMNS = 2;

export const ComplexModalFilterCategorySection = ({
	filterCategory,
	filterObject,
	updateFilterObject,
}: ComplexModalFilterCategorySectionProps) => {
	const intl = useIntl();
	const [showingMore, setShowingMore] = useState(false);

	const { width } = useWindowDimensions();

	const FilterColumnOne: JSX.Element[] = [];
	const FilterColumnTwo: JSX.Element[] = [];

	// List of keys to see how many subfilters are in the category
	const keys = Object.keys(filterObject[filterCategory]);

	const expandSubsection = () => {
		setShowingMore(!showingMore);
	};

	Object.entries(filterObject[filterCategory]).forEach(
		([subFilterName, subFilterObject], index) => {
			// On tablet and desktop, split the subfilters into two columns
			if (width > 768) {
				// If there are less than 5 subfilters,
				// or if the current subfilter is in the first half of the list,
				// Then add it to the first column
				if (
					keys.length < MAX_SUBFILTERS_BEFORE_TWO_COLUMNS ||
					keys.length / NUMBER_OF_COLUMNS > index
				) {
					const isSubFilterHidden =
						FilterColumnOne.length >= MAX_COLUMN_ENTRIES_BEFORE_EXPAND &&
						keys.length > MAX_SUBFILTERS_BEFORE_EXPAND &&
						!showingMore;
					FilterColumnOne.push(
						<CustomerStoryFilterModalRadioButton
							key={index}
							index={index}
							filterCategory={filterCategory}
							subFilterName={subFilterName}
							grouping={subFilterObject.grouping}
							active={subFilterObject.stagedActive}
							isSubFilterHidden={isSubFilterHidden}
							updateFilterObject={updateFilterObject}
						/>,
					);
					// if the current subfilter is in the first half of the list,
					// Then add it to the first column
				} else {
					const isSubFilterHidden =
						FilterColumnTwo.length >= MAX_COLUMN_ENTRIES_BEFORE_EXPAND &&
						keys.length > MAX_SUBFILTERS_BEFORE_EXPAND &&
						!showingMore;

					FilterColumnTwo.push(
						<CustomerStoryFilterModalRadioButton
							key={index}
							index={index}
							filterCategory={filterCategory}
							subFilterName={subFilterName}
							grouping={subFilterObject.grouping}
							active={subFilterObject.stagedActive}
							isSubFilterHidden={isSubFilterHidden}
							updateFilterObject={updateFilterObject}
						/>,
					);
				}
			} else {
				// On mobile, add all subfilters to the first column
				const isSubFilterHidden =
					FilterColumnOne.length >= MAX_COLUMN_ENTRIES_BEFORE_EXPAND && !showingMore;

				FilterColumnOne.push(
					<CustomerStoryFilterModalRadioButton
						key={index}
						index={index}
						filterCategory={filterCategory}
						subFilterName={subFilterName}
						grouping={subFilterObject.grouping}
						active={subFilterObject.stagedActive}
						isSubFilterHidden={isSubFilterHidden}
						updateFilterObject={updateFilterObject}
					/>,
				);
			}
		},
	);

	return (
		<div>
			<h4>{filterCategory}</h4>
			<div className="filter-modal-grouping-container">
				<div className="filter-modal-grouping-column filter-column-1">{FilterColumnOne}</div>
				<div className="filter-modal-grouping-column filter-column-2">{FilterColumnTwo}</div>
			</div>
			{keys.length > MAX_SUBFILTERS_BEFORE_EXPAND && (
				<button type="button" css={showButtonStyle} onClick={expandSubsection}>
					{!showingMore
						? intl.formatMessage({
								id: 'CustomersLandingPage.showMore',
								defaultMessage: 'Show more',
							})
						: intl.formatMessage({
								id: 'CustomersLandingPage.showLess',
								defaultMessage: 'Show less',
							})}
				</button>
			)}
		</div>
	);
};

const showButtonStyle = css({
	border: 'none',
	background: 'transparent',
	padding: 0,
	fontSize: '1rem',
	fontFamily: 'Charlie Text',
	cursor: 'pointer',
	color: '#172B4D',
	marginBlockStart: '1.5rem',
});
